import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, CalendarDays, Search, Loader2 } from "lucide-react";
import { useLang, localized } from "../i18n/LanguageContext";
import api from "../lib/api";
import { Button } from "./ui/button";

// default dates: tomorrow 10:00 -> +3 days 18:00
const pad = (n) => String(n).padStart(2, "0");
const isoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const BookingEngine = ({ compact = false, fixedCityId = null }) => {
  const { lang, tr, lp } = useLang();
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [cityId, setCityId] = useState(fixedCityId || "");
  const tomorrow = new Date(Date.now() + 86400000);
  const later = new Date(Date.now() + 4 * 86400000);
  const [startDate, setStartDate] = useState(isoDate(tomorrow));
  const [startTime, setStartTime] = useState("10:00");
  const [endDate, setEndDate] = useState(isoDate(later));
  const [endTime, setEndTime] = useState("18:00");

  useEffect(() => {
    api.get("/cities").then((r) => {
      setCities(r.data);
      if (!fixedCityId && r.data.length && !cityId) setCityId(r.data[0].id);
    });
  }, []);

  const submit = () => {
    const start = `${startDate}T${startTime}`;
    const end = `${endDate}T${endTime}`;
    const cid = fixedCityId || cityId;
    navigate(`${lp("/reservation")}?city=${cid}&start=${start}&end=${end}`);
  };

  return (
    <div data-testid="booking-engine" className="bg-white rounded-3xl shadow-xl border border-slate-100 p-5 md:p-7">
      <div className={`grid gap-4 ${fixedCityId ? "md:grid-cols-2" : "md:grid-cols-3"} items-end`}>
        {!fixedCityId && (
          <div>
            <label className="text-base font-semibold text-ink mb-2 flex items-center gap-1.5"><MapPin className="w-4 h-4 text-brand" /> {tr.booking.destination}</label>
            <select data-testid="be-city" value={cityId} onChange={(e) => setCityId(e.target.value)}
              className="w-full h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-4 text-lg font-medium bg-white">
              <option value="">{tr.booking.chooseCity}</option>
              {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="text-base font-semibold text-ink mb-2 flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-brand" /> {tr.booking.delivery}</label>
          <div className="flex gap-2">
            <input data-testid="be-start-date" type="date" value={startDate} min={isoDate(new Date())} onChange={(e) => setStartDate(e.target.value)} className="flex-1 min-w-0 h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-3 text-base" />
            <input data-testid="be-start-time" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="w-24 h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-2 text-base" />
          </div>
        </div>
        <div>
          <label className="text-base font-semibold text-ink mb-2 flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-brand" /> {tr.booking.pickup}</label>
          <div className="flex gap-2">
            <input data-testid="be-end-date" type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} className="flex-1 min-w-0 h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-3 text-base" />
            <input data-testid="be-end-time" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="w-24 h-14 rounded-xl border-2 border-slate-200 focus:border-brand px-2 text-base" />
          </div>
        </div>
      </div>
      <Button data-testid="be-search-btn" onClick={submit} disabled={!(fixedCityId || cityId)}
        className="w-full h-14 mt-4 rounded-xl bg-accent2 hover:bg-accent2-hover text-white font-bold text-lg transition-colors">
        <Search className="w-5 h-5 mr-2" /> {tr.booking.search}
      </Button>
    </div>
  );
};

export default BookingEngine;
